import { VANGUARD_ALPHA, PROJECTION_DEFAULTS, type RiskProfile } from '@/data/valueOfAdvice'
import type {
  CalculatorInputs,
  CalculationResults,
  TimelineDataPoint,
  PortfolioBreakdown,
  ValueAttribution,
} from '@/types'

// ============================================
// Calculation Functions
// ============================================

/**
 * Calculate future value of an investment with annual contributions
 */
export const calculateFutureValue = (
  presentValue: number,
  annualContribution: number,
  years: number,
  annualReturn: number
): number => {
  let futureValue = presentValue
  for (let i = 0; i < years; i++) {
    futureValue = futureValue * (1 + annualReturn) + annualContribution
  }
  return Math.round(futureValue)
}

/**
 * Calculate the value of advice over a given period
 */
export const calculateAdviceValue = (
  portfolioValue: number,
  annualContribution: number,
  years: number,
  riskProfile: RiskProfile = 'balanced'
): CalculationResults => {
  const baseReturn = PROJECTION_DEFAULTS.marketReturn[riskProfile]
  const advisedReturn = baseReturn + VANGUARD_ALPHA.totalPotential

  const withoutAdvice = calculateFutureValue(portfolioValue, annualContribution, years, baseReturn)
  const withAdvice = calculateFutureValue(portfolioValue, annualContribution, years, advisedReturn)
  const valueAdded = withAdvice - withoutAdvice
  const percentageGain = ((withAdvice - withoutAdvice) / withoutAdvice) * 100

  return {
    withoutAdvice,
    withAdvice,
    valueAdded,
    percentageGain: Math.round(percentageGain * 10) / 10,
    years,
  }
}

/**
 * Generate a timeline of portfolio values over the projection period
 */
export const generateProjectionTimeline = (
  portfolioValue: number,
  annualContribution: number,
  years: number,
  riskProfile: RiskProfile = 'balanced'
): TimelineDataPoint[] => {
  const baseReturn = PROJECTION_DEFAULTS.marketReturn[riskProfile]
  const advisedReturn = baseReturn + VANGUARD_ALPHA.totalPotential

  const timeline: TimelineDataPoint[] = []
  let withoutAdvice = portfolioValue
  let withAdvice = portfolioValue

  for (let year = 0; year <= years; year++) {
    timeline.push({
      year,
      withAdvice: Math.round(withAdvice),
      withoutAdvice: Math.round(withoutAdvice),
      valueAdded: Math.round(withAdvice - withoutAdvice),
    })

    withoutAdvice = withoutAdvice * (1 + baseReturn) + annualContribution
    withAdvice = withAdvice * (1 + advisedReturn) + annualContribution
  }

  return timeline
}

/**
 * Calculate value attribution breakdown for each Vanguard Alpha component
 */
export const calculateValueAttribution = (totalValueAdded: number): ValueAttribution[] => {
  const totalPercent = VANGUARD_ALPHA.components.reduce((sum, c) => sum + c.valuePercent, 0)

  return VANGUARD_ALPHA.components.map((component) => ({
    id: component.id,
    name: component.name,
    description: component.description,
    valueBps: component.valueBps,
    valuePercent: component.valuePercent,
    color: component.color,
    attributedValue: Math.round((component.valuePercent / totalPercent) * totalValueAdded),
    percentOfTotal: Math.round((component.valuePercent / totalPercent) * 100),
  }))
}

/**
 * Calculate total portfolio value from breakdown
 */
export const calculateTotalPortfolio = (breakdown: PortfolioBreakdown): number => {
  return breakdown.pension + breakdown.isa + breakdown.gia
}

// ============================================
// Formatting Functions
// ============================================

/**
 * Format a number as GBP currency
 */
export const formatCurrency = (value: number): string => {
  return `£${Math.round(value).toLocaleString('en-GB')}`
}

/**
 * Format a number with commas for display
 */
export const formatNumberWithCommas = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return ''
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * Parse a formatted number string (remove commas)
 */
export const parseFormattedNumber = (str: string): number => {
  if (!str || str === '') return 0
  const cleaned = str.replace(/,/g, '').replace(/^0+(?=\d)/, '')
  const num = parseFloat(cleaned)
  return isNaN(num) ? 0 : num
}

/**
 * Format basis points as percentage
 */
export const formatBasisPoints = (bps: number): string => {
  return `${(bps / 100).toFixed(2)}%`
}

/**
 * Format a percentage with one decimal place
 */
export const formatPercent = (value: number): string => {
  return `${value.toFixed(1)}%`
}

// ============================================
// Validation Functions
// ============================================

/**
 * Validate calculator inputs
 */
export const validateInputs = (inputs: CalculatorInputs): string[] => {
  const errors: string[] = []

  if (inputs.currentAge < 18 || inputs.currentAge > 100) {
    errors.push('Current age must be between 18 and 100')
  }

  if (inputs.retirementAge <= inputs.currentAge) {
    errors.push('Retirement age must be greater than current age')
  }

  if (inputs.retirementAge > 100) {
    errors.push('Retirement age must be 100 or less')
  }

  if (inputs.portfolioValue < 0) {
    errors.push('Portfolio value cannot be negative')
  }

  if (inputs.annualContribution < 0) {
    errors.push('Annual contribution cannot be negative')
  }

  return errors
}

/**
 * Get years until retirement
 */
export const getYearsToRetirement = (currentAge: number, retirementAge: number): number => {
  return Math.max(0, retirementAge - currentAge)
}
